import { ApiError, CLIENT_CODES, codeForResponse, type ErrorEnvelope } from "./errors";

export type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
export type Query = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  body?: unknown;
  /** Sent as multipart/form-data instead of JSON (docs/api.md 45.5). */
  form?: FormData;
  query?: Query;
  signal?: AbortSignal;
  /** Overrides the client default; 0 disables the timeout (the live stream). */
  timeoutMs?: number;
  /** Skips the bearer header even when a token exists (public routes). */
  anonymous?: boolean;
  accept?: string;
}

export interface HttpClientConfig {
  /** `VITE_API_BASE_URL ?? "/v1"`: relative behind a proxy, absolute cross-origin. */
  baseUrl: string;
  /** The session token of this app (attendee or staff), read on every call. */
  getToken?: () => string | null;
  /** Called on any 401 from an authenticated call, to clear a dead session. */
  onUnauthorized?: () => void;
  timeoutMs?: number;
}

export interface HttpClient {
  readonly baseUrl: string;
  json<T>(method: Method, path: string, opts?: RequestOptions): Promise<T>;
  /** A response read as text (the template preview is HTML). */
  text(method: Method, path: string, opts?: RequestOptions): Promise<string>;
  /** The raw Response, after the error check: downloads and streams. */
  raw(method: Method, path: string, opts?: RequestOptions): Promise<Response>;
  /** A call answered `204` (or whose body is not wanted). */
  void(method: Method, path: string, opts?: RequestOptions): Promise<void>;
}

const DEFAULT_TIMEOUT_MS = 15_000;

function buildUrl(baseUrl: string, path: string, query?: Query): string {
  const base = baseUrl.replace(/\/+$/, "");
  let url = `${base}${path.startsWith("/") ? path : `/${path}`}`;
  if (query) {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === "") continue;
      params.set(k, String(v));
    }
    const qs = params.toString();
    if (qs) url += `?${qs}`;
  }
  return url;
}

function parseRetryAfter(res: Response): number | null {
  const raw = res.headers.get("Retry-After");
  if (!raw) return null;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

async function toApiError(res: Response): Promise<ApiError> {
  const env = (await res.json().catch(() => ({}))) as ErrorEnvelope;
  return new ApiError({
    status: res.status,
    code: codeForResponse(res.status, env.error?.code),
    message: env.error?.message ?? res.statusText ?? "Request failed.",
    details: env.error?.details ?? [],
    retryAfterSec: parseRetryAfter(res),
    attemptsRemaining: env.error?.attempts_remaining ?? null,
  });
}

/**
 * The one fetch wrapper (docs/frontend-integration.md 2.3): bearer header,
 * JSON in and out, the error envelope parsed into an ApiError, a network
 * failure and a timeout as their own codes. No cookies, ever.
 */
export function createHttpClient(config: HttpClientConfig): HttpClient {
  const defaultTimeout = config.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  async function send(method: Method, path: string, opts: RequestOptions = {}): Promise<Response> {
    const headers: Record<string, string> = { Accept: opts.accept ?? "application/json" };
    let body: BodyInit | undefined;
    if (opts.form) {
      body = opts.form;
    } else if (opts.body !== undefined) {
      headers["Content-Type"] = "application/json";
      body = JSON.stringify(opts.body);
    }
    const token = opts.anonymous ? null : (config.getToken?.() ?? null);
    if (token) headers.Authorization = `Bearer ${token}`;

    const timeoutMs = opts.timeoutMs ?? defaultTimeout;
    const controller = new AbortController();
    const onAbort = () => controller.abort(opts.signal?.reason);
    opts.signal?.addEventListener("abort", onAbort, { once: true });
    let timedOut = false;
    const timer =
      timeoutMs > 0
        ? setTimeout(() => {
            timedOut = true;
            controller.abort();
          }, timeoutMs)
        : null;

    let res: Response;
    try {
      res = await fetch(buildUrl(config.baseUrl, path, opts.query), {
        method,
        headers,
        body,
        signal: controller.signal,
        credentials: "omit",
      });
    } catch {
      if (timedOut) {
        throw new ApiError({ status: 0, code: CLIENT_CODES.timeout, message: "The server took too long to answer." });
      }
      if (opts.signal?.aborted) {
        throw new ApiError({ status: 0, code: CLIENT_CODES.aborted, message: "Request cancelled." });
      }
      throw new ApiError({ status: 0, code: CLIENT_CODES.network, message: "Could not reach the server." });
    } finally {
      if (timer) clearTimeout(timer);
      opts.signal?.removeEventListener("abort", onAbort);
    }

    if (!res.ok) {
      const err = await toApiError(res);
      if (res.status === 401 && token) config.onUnauthorized?.();
      throw err;
    }
    return res;
  }

  return {
    baseUrl: config.baseUrl,
    async json<T>(method: Method, path: string, opts?: RequestOptions) {
      const res = await send(method, path, opts);
      if (res.status === 204) return undefined as T;
      try {
        return (await res.json()) as T;
      } catch {
        throw new ApiError({ status: res.status, code: CLIENT_CODES.badResponse, message: "The server sent an unreadable answer." });
      }
    },
    async text(method, path, opts) {
      const res = await send(method, path, { ...opts, accept: opts?.accept ?? "text/html" });
      return res.text();
    },
    raw: send,
    async void(method, path, opts) {
      await send(method, path, opts);
    },
  };
}
